import { CircularProgress } from '@mui/material';
import { createPortal } from 'react-dom';
import DialogStyle from './ConfirmDialog.module.scss';
import { useFullscreenContainer } from 'core/utils/fullscreenContainer';

interface IConfirmDialog {
	type: 'shift' | 'event' | 'time-off';
	isLoading: boolean;
	handleDeleteClick: () => void;
	setConfirmDeleteIsOpen: (args: boolean) => void;
	title?: string;
	message?: string;
}

const defaultCopy = (type: IConfirmDialog['type']): { title: string; message: string } => {
	if (type === 'shift') {
		return {
			title: 'Delete Shift',
			message: 'Are you sure you want to delete this shift?',
		};
	}
	if (type === 'time-off') {
		return {
			title: 'Delete Time Off',
			message: 'Are you sure you want to delete this time off?',
		};
	}
	return {
		title: 'Delete Event',
		message: 'Are you sure you want to delete this event?',
	};
};

export default function ConfirmDialog({
	type,
	isLoading,
	handleDeleteClick,
	setConfirmDeleteIsOpen,
	title,
	message,
}: IConfirmDialog): JSX.Element {
	const portalContainer = useFullscreenContainer(true);
	const copy = defaultCopy(type);
	const dialogTitle = title || copy.title;
	const dialogMessage = message || copy.message;

	const dialog = (
		<div className={DialogStyle.dialogWrapper}>
			<div className={DialogStyle.dialogContent}>
				<div className={DialogStyle.dialogHeader}>
					<h3 className={DialogStyle.dialogTitle}>{dialogTitle}</h3>
				</div>
				<div className={DialogStyle.dialogBody}>
					<p>{dialogMessage}</p>
				</div>
				<div className={DialogStyle.dialogFooter}>
					{isLoading ? (
						<div className={DialogStyle.loadingContainer}>
							<CircularProgress size={20} />
							<span>Deleting...</span>
						</div>
					) : (
						<>
							<button 
								type="button"
								className={DialogStyle.cancelButton} 
								onClick={() => setConfirmDeleteIsOpen(false)}
								disabled={isLoading}
							>
								Cancel
							</button>
							<button 
								type="button"
								className={DialogStyle.deleteButton} 
								onClick={handleDeleteClick}
								disabled={isLoading}
							>
								Delete
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);

	if (portalContainer) {
		return createPortal(dialog, portalContainer);
	}

	return dialog;
}
